"use client";

import { useState, type SyntheticEvent } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import type { LetterVersion } from "@/lib/customer-letters";

export interface LetterBodyCopy {
  /** Fully composed, e.g. "Translated from Spanish". */
  translatedFrom: string;
  readOriginal: string;
  readTranslation: string;
  readFullLetter: string;
}

interface LetterBodyProps {
  letterId: string;
  locale: string;
  /** The version the visitor sees first. */
  shown: LetterVersion;
  /** The customer's own words, passed only when `shown` is a translation. */
  original: LetterVersion | null;
  signature: string;
  copy: LetterBodyCopy;
  letterFontClassName: string;
}

/**
 * The letter itself: a pull quote, the toggle between a translation and the
 * customer's original, and the full text behind a native disclosure.
 *
 * `<details>` rather than a stateful accordion: it opens without JavaScript,
 * the browser supplies keyboard and screen-reader behaviour, and find-in-page
 * reaches text inside it while it is closed.
 *
 * Every element holding letter text declares the language of the version on
 * screen, which on en-US flips with the toggle — a screen reader otherwise
 * reads the Spanish original aloud in an English voice.
 */
export function LetterBody({
  letterId,
  locale,
  shown,
  original,
  signature,
  copy,
  letterFontClassName,
}: LetterBodyProps) {
  const [showingOriginal, setShowingOriginal] = useState(false);
  const active = showingOriginal && original !== null ? original : shown;

  const reportExpand = (event: SyntheticEvent<HTMLDetailsElement>) => {
    if (event.currentTarget.open) {
      trackEvent({ event: "letter_expand", letterId, locale });
    }
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <figure className="relative rounded-[20px] border border-white/10 bg-site-card p-6 md:p-8">
        <span
          aria-hidden="true"
          className="absolute inset-y-[18px] left-0 w-[3px] rounded-r-[3px] bg-yellow-500 opacity-80"
        />
        <blockquote
          lang={active.lang}
          className="text-xl leading-relaxed text-white md:text-2xl text-pretty"
        >
          <p>{`“${active.text.pullQuote.join(" … ")}”`}</p>
        </blockquote>
        <figcaption className="mt-5 text-sm font-semibold text-white">
          {`— ${signature}`}
        </figcaption>
        {original !== null && (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-white/60">
            {!showingOriginal && <span>{copy.translatedFrom}</span>}
            {!showingOriginal && <span aria-hidden="true">·</span>}
            <button
              type="button"
              onClick={() => setShowingOriginal((current) => !current)}
              className="font-semibold text-yellow-500 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500"
            >
              {showingOriginal ? copy.readTranslation : copy.readOriginal}
            </button>
          </p>
        )}
      </figure>

      <details onToggle={reportExpand} className="group rounded-[20px] border border-white/10">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 text-sm font-semibold text-yellow-500 [&::-webkit-details-marker]:hidden">
          {copy.readFullLetter}
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <div
          lang={active.lang}
          className={cn(
            "max-w-prose space-y-4 px-6 pb-8 pt-2 text-[17px] leading-relaxed text-white/85",
            letterFontClassName,
          )}
        >
          <p>{active.text.greeting}</p>
          {active.text.paragraphs.map((paragraph, index) => (
            // Index, not text: letters are verbatim, so two paragraphs can be
            // identical, and the list is static.
            <p key={index}>{paragraph}</p>
          ))}
          <p lang={active.text.closingLang}>{active.text.closing}</p>
          <p className="font-semibold text-white">{signature}</p>
        </div>
      </details>
    </div>
  );
}
